<template>
  <div class="flex flex-col gap-4" :style="{ minHeight: sizeKeeperHeight + 'px' }">
    <p v-if="isLoading" class="m-0 flex items-center gap-2 text-muted-foreground">
      <loader-circle-icon class="size-4 animate-spin" />
      {{ tr('Loading...') }}
    </p>
    <empty-screen
      v-else-if="items.length === 0"
      class="my-5"
      icon="adjustments"
      :title="tr('No posts match your current filters')"
      :flag-text="tr('Adjust filters')"
      :description="tr('We couldn\'t find any posts that match your selected filters. Try adjusting your filter settings to see more results in your post list.')"
    />
    <div v-else ref="content" class="flex flex-col gap-3">
      <p class="m-0 text-sm text-muted-foreground">{{ itemsCountText }}</p>

      <div class="glass-tile rounded-xl px-2 py-1">
        <ui-table>
          <ui-table-header>
            <ui-table-row class="hover:bg-transparent [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground">
              <ui-table-head>{{ tr('Source') }}</ui-table-head>
              <ui-table-head>{{ tr('Post') }}</ui-table-head>
              <ui-table-head>{{ tr('Post type') }}</ui-table-head>
              <ui-table-head>{{ tr('Media type') }}</ui-table-head>
              <ui-table-head>{{ tr('Posted') }}</ui-table-head>
            </ui-table-row>
          </ui-table-header>
          <ui-table-body>
            <ui-table-row v-for="post in items" :key="post.id">
              <ui-table-cell>
                <span class="block text-xs text-muted-foreground">{{ ucfirst(post.source_profile.platform) }}</span>
                {{ post.source_profile.name }}
              </ui-table-cell>
              <ui-table-cell class="max-w-80 min-w-60 whitespace-normal">
                <span class="block text-xs text-muted-foreground">
                  {{ tr('Post ID') }}:
                  <a
                    v-if="post.url"
                    :href="post.url"
                    target="_blank"
                    class="text-primary hover:underline"
                  >
                    {{ post.external_id }}
                  </a>
                  <span v-else>{{ post.external_id }}</span>
                </span>
                <span class="line-clamp-2">{{ post.shortText }}</span>
              </ui-table-cell>
              <ui-table-cell>{{ post.type }}</ui-table-cell>
              <ui-table-cell>{{ post.media_type }}</ui-table-cell>
              <ui-table-cell>
                <span v-tooltip="formatToYMD(post.created_at) + ', ' + formatTime(post.created_at)">
                  {{ timeAgo(post.created_at, tr) }}
                </span>
              </ui-table-cell>
            </ui-table-row>
          </ui-table-body>
        </ui-table>
      </div>
    </div>

    <section v-if="!isLoading && sourceProfileDetails.length !== 0" class="flex flex-col gap-3">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Datasource informations') }}</h3>
      <div
        v-for="(sourceProfile, key) in sourceProfileDetails"
        :key="'source-profile-details--' + key"
        class="glass-tile flex flex-col gap-3 rounded-xl p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="font-semibold text-foreground">{{ sourceProfile.name }} - {{ ucfirst(sourceProfile.platform) }}</span>
          <refresh-button
            :label="tr('Update posts')"
            :success-message="tr('Posts of :name updated successfully.', { ':name': sourceProfile.name })"
            :promise="() => refreshSourceProfile(sourceProfile.id)"
          />
        </div>

        <p class="m-0 text-sm text-muted-foreground">
          <template v-if="sourceProfile.fetched_at">
            {{ tr('Data last updated at :time', { ':time': timeAgo(sourceProfile.fetched_at, tr) }) }}
          </template>
          <template v-else>
            {{ tr('The datasource has not been fetched yet.') }}
          </template>
        </p>

        <dl class="m-0 grid grid-cols-3 gap-3 text-sm max-sm:grid-cols-1">
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Number of posts examined') }}</dt>
            <dd class="m-0">{{ tr('last :num pcs', { ':num': sourceProfile.post_limit }) }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Latest known post date') }}</dt>
            <dd class="m-0 tabular-nums">{{ formatDateTimeOrDash(sourceProfile.latest_post_date) }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs font-semibold text-muted-foreground">{{ tr('Oldest examined post date') }}</dt>
            <dd class="m-0 tabular-nums">{{ formatDateTimeOrDash(sourceProfile.oldest_post_date) }}</dd>
          </div>
        </dl>
      </div>
    </section>
  </div>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import { RULE_REVIEW_CODE } from '@/consts.js';
import RuleService from '@/services/Rule.js';
import SourceProfileService from '@/services/SourceProfile.js';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import RefreshButton from '@/ui/components/RefreshButton.vue';
import { formatTime, formatToYMD, timeAgo } from '@/utils/date';
import debounce from '@/utils/debounce.js';
import { ucfirst } from '@/utils/string';

export default {
  name: 'RulePreview',
  components: {
    EmptyScreen,
    LoaderCircleIcon: LoaderCircle,
    RefreshButton,
  },
  props: {
    entity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      items: [],
      sourceProfileDetailsIsLoading: false,
      sourceProfileDetails: [],
      debouncedGeneratePreview: debounce(() => this.generateNewPreview(), 1000),
      sizeKeeperHeight: 0,
    };
  },
  computed: {
    itemsCountText() {
      if (this.items.length < 2) {
        return this.tr(':num item', { ':num': this.items.length });
      }

      return this.tr(':num items', { ':num': this.items.length });
    },
  },
  watch: {
    'entity.source_profiles': {
      deep: true,
      handler() {
        this.debouncedGeneratePreview();
        this.getSourceProfileDetails();
      },
    },
    'entity.facebook_post_types'() {
      this.debouncedGeneratePreview();
    },
    'entity.facebook_post_media_types'() {
      this.debouncedGeneratePreview();
    },
    'entity.instagram_post_types'() {
      this.debouncedGeneratePreview();
    },
    'entity.instagram_post_media_types'() {
      this.debouncedGeneratePreview();
    },
    'entity.output_campaign.objective'() {
      this.debouncedGeneratePreview();
    },
    'entity.conditions': {
      deep: true,
      handler() {
        this.debouncedGeneratePreview();
      },
    },
    'entity.settings.dont_create_expired_ads'() {
      this.debouncedGeneratePreview();
    },
  },
  mounted() {
    this.generateNewPreview();
    this.getSourceProfileDetails();
  },
  methods:{
    formatToYMD,
    formatTime,
    timeAgo,
    ucfirst,
    formatDateTimeOrDash(date) {
      return date ? formatToYMD(date) + ' ' + formatTime(date) : '–';
    },
    getSourceProfileDetails() {
      if (
        this.entity.source_profiles.length === 0
        || !this.entity.source_profiles[0].id
      ) {
        this.sourceProfileDetails = [];
        return;
      }

      this.sourceProfileDetailsIsLoading = true;

      SourceProfileService.get(this.entity.source_profiles[0].id)
        .then(resp => this.sourceProfileDetails = [ resp ])
        .catch(err => {
          this.error(err);
          this.sourceProfileDetails = [];
        })
        .finally(() => {
          this.sourceProfileDetailsIsLoading = false;
        });
    },
    generateNewPreview() {
      if (this.isLoading) {
        return;
      }

      const sourceProfileIds = this.entity.source_profiles.map(item => item.id).filter(item => !!item);

      if (
        sourceProfileIds.length === 0
        || this.entity.output_campaign.objective === null
        || !this.entity.isValidConditions
      ) {
        return;
      }

      if (this.$refs.content) {
        this.sizeKeeperHeight = this.$refs.content.offsetHeight;
      }

      this.isLoading = true;

      RuleService.generateActivityReview(this.entity)
        .then(resp => {
          this.items = resp
            .filter(item => item.code === RULE_REVIEW_CODE.WAITING_FOR_PROCESS)
            .map(item => item.entity);

          return resp;
        })
        .catch(err => {
          this.error(err);
          this.items = [];
        })
        .finally(() => {
          this.isLoading = false;

          this.$nextTick(() => {
            this.sizeKeeperHeight = this.$refs.content ? this.$refs.content.offsetHeight : 0;
          });
        });
    },
    refreshSourceProfile(id) {
      this.infoToast(this.tr('We will start updating the posts.') + ' ' + this.tr('Be patient, this may take up to a minute.'));

      return SourceProfileService.refresh(id)
        .then(resp => {
          this.generateNewPreview();
          return resp;
        });
    },
  },
};
</script>
